<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：管理対象一覧画面
 *
 * 作成者：2021/11/29 ノア）賀
 * 最終修正者：2022/2/4 ノア）黄
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left pos-rel">
          <el-button class="backButton" @click="onBackButtonClick" v-tooltip="getTooltip(tooltipType.back)">
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_041"></tti-svg-icon>
            <span>{{ $t('managementTarget.viewTitle', [managementTargetShowName]) }}</span>
          </div>
          <span class="tti-viewTitle notes-title">{{ $t('managementTarget.notesTitle') }}</span>
        </div>
        <div class="pull-right">
          <!-- 実績出力 -->
          <el-button
            class="tti-btn-jump"
            @click="onReallyOutputButtonClick"
            v-tooltip="{
              content: `${$t('managementTarget.reallyOutputTip')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <tti-svg-icon icon-name="wo_012"></tti-svg-icon>
            {{ $t('buttons.reallyOutput') }}
            <tti-svg-icon icon-name="wo_035"></tti-svg-icon>
          </el-button>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-content-title-container">
        <div class="tti-content-title">
          <span class="contentTitle">{{ $t('managementTarget.viewTitle', [managementTargetShowName]) }}</span>
        </div>
        <div class="functionArea">
          <el-pagination
            background
            layout="prev, pager, next"
            :total="tableData.length"
            :page-size="visablePageSize"
            :hide-on-single-page="true"
            :current-page.sync="currentPage"
            @current-change="onCurrentPageChange"
          >
          </el-pagination>
          <div class="searchBox">
            <el-input v-model.trim="searchText" onkeyup="this.value=this.value.replace(/[,]/g,'')">
              <el-button slot="append" icon="el-icon-search" @click="onSearchBoxClick"></el-button>
            </el-input>
          </div>
        </div>
      </div>
      <el-table
        :data="visableTableData"
        height="calc(100vh - 226px)"
        border
        :stripe="true"
        :empty-text="$t('messages.common.emptyTableData')"
        header-row-class-name="tti-master-table-header"
        @selection-change="handleSelectionChange"
        :cell-class-name="getCellClassName"
        :row-class-name="tableRowClassName"
      >
        <el-table-column type="selection" width="35"></el-table-column>
        <!-- 所属営業所名 -->
        <el-table-column
          prop="businessOfficeName"
          :label="$t('managementTarget.businessOfficeName')"
          align="left"
          width="218"
          :sortable="true"
          ><template slot-scope="scope">
            <div :id="'tableData[' + scope.$index + '].businessOfficeName'">
              {{ scope.row.businessOfficeName }}
            </div></template
          >
        </el-table-column>
        <!-- 管理対象コード -->
        <el-table-column
          prop="manageTargetCode"
          :label="$t('managementTarget.manageTargetCode', [managementTargetShowName])"
          align="left"
          width="200"
          :sortable="true"
        >
        </el-table-column>
        <!-- 名称 -->
        <el-table-column
          prop="manageTargetName"
          :label="$t('managementTarget.manageTargetName')"
          align="left"
          width="218"
          :sortable="true"
        >
        </el-table-column>
        <!-- 稼働状況 -->
        <el-table-column
          prop="deviceStatus"
          :label="$t('managementTarget.deviceStatus')"
          align="left"
          width="120"
          :sortable="true"
        >
          <template slot-scope="scope">
            <div size="small">
              {{ getDeviceStatusLabel(scope.row.deviceStatus) }}
            </div>
          </template>
        </el-table-column>
        <!-- 現在位置 -->
        <el-table-column
          prop="location"
          :label="$t('managementTarget.location')"
          align="left"
          width="350"
          :sortable="true"
        >
        </el-table-column>
        <!-- 管理場所詳細 -->
        <el-table-column
          prop="managementPlaceDetail"
          :label="$t('managementTarget.managementPlaceDetail')"
          align="left"
          width="350"
          :sortable="true"
        >
        </el-table-column>
        <!-- 累計稼働時間 -->
        <el-table-column
          prop="totalTime"
          :label="$t('managementTarget.totalTime')"
          align="right"
          width="150"
          :sortable="true"
        >
          <template slot-scope="scope">
            {{ getThousandSeparator(scope.row.totalTime) }}
          </template>
        </el-table-column>
        <!-- 点検整備時期 -->
        <el-table-column
          prop="arrangementDate"
          :label="$t('managementTarget.arrangementDate')"
          align="left"
          width="120"
          :sortable="true"
        >
          <template slot-scope="scope">
            <div size="small">
              {{ scope.row.arrangementDate }}
            </div>
          </template>
        </el-table-column>
        <!-- 車検時期 -->
        <el-table-column
          prop="checkDate"
          :label="$t('managementTarget.checkDate')"
          align="left"
          width="120"
          :sortable="true"
        >
          <template slot-scope="scope">
            <div size="small">
              {{ scope.row.checkDate }}
            </div>
          </template>
        </el-table-column>
        <!-- 節電モード -->
        <el-table-column
          prop="powerSavingMode"
          :label="$t('managementTarget.powerSavingMode')"
          align="left"
          width="170"
          :sortable="true"
        >
          <template slot-scope="scope">
            <div size="small">
              {{ getPowerSavingModeLabel(scope.row.powerSavingMode) }}
            </div>
          </template>
        </el-table-column>
        <!-- 所有者（会社＆営業所） -->
        <el-table-column prop="owner" :label="$t('managementTarget.owner')" align="left" width="218" :sortable="true">
        </el-table-column>
        <!-- 貸出先営業所コード -->
        <el-table-column
          prop="lendBusinessOfficeCode"
          :label="$t('managementTarget.lendBusinessofficeCode')"
          align="left"
          width="160"
          :sortable="true"
        >
        </el-table-column>
        <!-- 貸出先 -->
        <el-table-column
          prop="lendBusinessOfficeName"
          :label="$t('managementTarget.lendBusinessofficeName')"
          align="left"
          width="218"
          :sortable="true"
        >
        </el-table-column>
        <!-- 貸出日 -->
        <el-table-column
          prop="lendDate"
          :label="$t('managementTarget.lendDate')"
          align="left"
          width="120"
          :sortable="true"
        >
        </el-table-column>
        <!-- 最終更新日時 -->
        <el-table-column
          prop="lastUpdateDate"
          :label="$t('managementTarget.lastUpdateDate')"
          align="left"
          width="180"
          :sortable="true"
        >
        </el-table-column>
      </el-table>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area"></div>
      <div class="tti-bottom-right-area">
        <!-- 出力 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onOutputButtonClick"
          v-tooltip="getTooltip(tooltipType.output)"
        >
          <tti-svg-icon icon-name="su_003"></tti-svg-icon>{{ $t('buttons.output') }}
        </el-button>
      </div>
    </section>

    <!-- 稼働実績の期間指定画面をポップアップする -->
    <ttiDialog
      :isShow="showDialog"
      @handelDisplay="onHandelDisplay"
      :manualKey="manualKey"
      :title="$t('managementTarget.reallyOutput')"
      width="500px"
    >
      <template slot="body">
        <el-form
          :model="reallyOutputParams"
          :rules="reallyOutputDialogRules"
          ref="reallyOutputDialogFormRef"
          label-width="150px"
          size="mini"
        >
          <template>
            <!-- 稼働開始日 -->
            <el-form-item :label="$t('managementTarget.startDate')" prop="startDate">
              <el-date-picker
                v-model="reallyOutputParams.startDate"
                type="date"
                format="yyyy/MM/dd"
                value-format="yyyy/MM/dd"
                size="small"
                :picker-options="pickerOptions"
                :editable="false"
                :clearable="false"
                v-tooltip="{
                  content: `${$t('managementTarget.operationStartDayTip')}`,
                  placement: 'bottom-start',
                  delay: { show: 1000, hide: 0 }
                }"
              ></el-date-picker>
            </el-form-item>
            <!-- 稼働終了日 -->
            <el-form-item :label="$t('managementTarget.endDate')" prop="endDate">
              <el-date-picker
                v-model="reallyOutputParams.endDate"
                type="date"
                format="yyyy/MM/dd"
                value-format="yyyy/MM/dd"
                size="small"
                :picker-options="pickerOptions"
                :editable="false"
                :clearable="false"
                v-tooltip="{
                  content: `${$t('managementTarget.operationEndDayTip')}`,
                  placement: 'bottom-start',
                  delay: { show: 1000, hide: 0 }
                }"
              ></el-date-picker>
            </el-form-item>
          </template>
        </el-form>
      </template>
      <template slot="foot">
        <!-- 出力 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onDialogOutputButtonClick"
          v-tooltip="getTooltip(tooltipType.output)"
        >
          <tti-svg-icon icon-name="su_003"></tti-svg-icon>{{ $t('buttons.output') }}
        </el-button>
      </template>
    </ttiDialog>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch, Ref } from 'vue-property-decorator'
import { Form } from 'element-ui'
import { cellCallbackParams } from 'element-ui/types/table'
import { differenceInYears } from 'date-fns'

import { i18n } from '@/locales'
import TtiDialog from '@/components/TtiDialog.vue'
import { getManagementTargetData, managementTargetDataDownload, ManagementTargetHistoryDownload } from '@/store/apis'
import {
  ManagementTarget,
  General,
  MasterGetParam,
  GpsTrackerTerminalConfigData,
  ReallyOutputPostParam,
  SearchTargets
} from '@/models'
import { getIntervalTime, isDateAfter, parseDate, pickerOptions } from '@/utils/dateUtils'
import { MaintenanceWarningDays, CheckCarWarningDays, DeviceStatus, PowerType } from '@/commons/constants'
import { common } from '@/store/modules'
import { localService } from '@/commons/services/localService'
import { generalService, GeneralType } from '@/commons/services/generalService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import HelperService from '@/service/helperService'
import manualConfig from '@/config/manualConfig'
import { FormItemType } from '@/views/master/MST_BASE.vue'

/**
 * 管理対象一覧画面
 *
 * @class
 */
@Component({
  name: 'mgmt-trgts',
  components: { TtiDialog }
})
export default class MGMT_TRGTS extends Vue {
  /**
   * タイムセレクタをクリックしたときの処理
   */
  pickerOptions = pickerOptions

  /**
   * 管理対象一覧
   */
  tableData: ManagementTarget[] = []

  /**
   * 当前表示データ
   */
  visableTableData: ManagementTarget[] = []

  /**
   * GPSトラッカー端末判定値設定
   */
  gpsTrackerTerminalConfigData: GpsTrackerTerminalConfigData[] = []

  /**
   * ページの表示件数
   */
  visablePageSize = 50

  /**
   * 当前ページ
   */
  currentPage = 1

  /**
   * 第一列の項目（Focus用）
   */
  firstField = 'businessOfficeName'

  /**
   * 検索文字列
   */
  oldSearchText = ''

  /**
   * 検索文字列
   */
  searchText = ''

  /**
   * 検索目標
   */
  searchTargets: SearchTargets[] = []

  /**
   * 検索結果INDEX
   */
  searchResultIndexList: number[] = []

  /**
   * 検索結果のPoint
   */
  searchResultPoint = -1

  /**
   * 検索結果INDEX
   */
  searchResultIndex = -1

  /**
   * 気に入り営業所
   *
   * @returns {string[]} 気に入り営業所
   */
  get favoriteOfficeIdList(): string[] {
    return common.favOfficeList
  }

  /**
   * 管理対象表示名
   *
   * @returns {string} 管理対象表示名
   */
  get managementTargetShowName(): string {
    return localService.userInfo?.managementTargetShowName
  }

  /**
   * 稼働実績サブ画面のマニュアルキーを取得
   *
   * @returns {string} 稼働実績サブ画面のマニュアルキー
   */
  get manualKey(): string {
    return manualConfig.route.reallyOutput
  }

  /**
   * 稼働状況
   */
  deviceStatus: General[] = []

  /**
   * 節電モード
   */
  managementPowerTypeList: General[] = []

  /**
   * ツールチップタイプ
   */
  tooltipType = {
    // 戻る
    back: 'back',
    // 出力
    output: 'output',
    // 実績出力
    reallyOutput: 'reallyOutput'
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  async mounted(): Promise<void> {
    this.fetchDeviceStatus()
    this.fetchManagementPowerType()
    await this.fetchManagementTargetData()
    this.$log.debug('MGMT_TRGTS > mounted')
    this.initSearchTargets()
  }

  /**
   * テーブルデータの初期化を実行する。
   *
   * @param {ManagementTarget[]} data 検索されたデータ
   * @returns {void}
   */
  initTableData(data: ManagementTarget[]): void {
    if (!data) {
      data = []
    }
    this.initVisableData()
    this.initSearchBox()
  }

  /**
   * テーブルデータの初期化を実行する。
   *
   * @returns {void}
   */
  initVisableData(): void {
    this.currentPage = 1
    this.oldSearchText = ''
    this.searchText = ''
    this.searchResultIndex = -1
    this.searchResultPoint = -1
    this.searchResultIndexList = []
    this.setVisableTableData()
  }

  /**
   * 検索ボックスの初期化を実行する。
   *
   * @returns {void}
   */
  initSearchBox(): void {
    this.oldSearchText = ''
    this.searchText = ''
    this.searchResultIndex = -1
    this.searchResultPoint = -1
    this.searchResultIndexList = []
  }

  /**
   * テーブルデータの初期化を実行する。
   *
   * @param {number} currentPage 当前ページ
   * @returns {void}
   */
  setVisableTableData(currentPage = 1): void {
    if (!this.tableData) {
      return
    }

    const startIndex = (currentPage - 1) * this.visablePageSize
    const endIndex = currentPage * this.visablePageSize

    this.visableTableData = this.tableData.slice(startIndex, endIndex)
  }

  /**
   * 千分記号
   *
   * @param {string} str 文字列
   * @returns {string} 文字列
   */
  getThousandSeparator(str: string): string {
    return HelperService.getThousandSeparator(str)
  }

  /**
   * 稼働状況リストを取得する
   *
   * @returns {void}
   */
  async fetchDeviceStatus(): Promise<void> {
    try {
      this.deviceStatus = await generalService[GeneralType.DEVICE_STATUS]
    } catch (e) {
      this.$log.debug('MST_MGMT_TRGTS > fetchDeviceStatus: e=', e)
      throw e
    }
  }

  /**
   * 節電モードリストを取得する
   *
   * @returns {void}
   */
  async fetchManagementPowerType(): Promise<void> {
    try {
      this.managementPowerTypeList = await generalService[GeneralType.POWER_SAVING_MODE]
    } catch (e) {
      this.$log.debug('MGMT_TRGTS > fetchManagementPowerType: e=', e)
      throw e
    }
  }

  /**
   * 管理対象一覧を取得する
   *
   * @returns {void}
   */
  @Watch('favoriteOfficeIdList', { immediate: false, deep: true })
  async fetchManagementTargetData(): Promise<void> {
    try {
      if (!this.favoriteOfficeIdList || this.favoriteOfficeIdList.length === 0) {
        return
      }

      const params = {
        businessOfficeIdList: this.favoriteOfficeIdList
      } as MasterGetParam

      const tableData = await getManagementTargetData(params)
      this.tableData = (tableData.list as ManagementTarget[]) || []
      this.initTableData(this.tableData)
    } catch (e) {
      this.$log.debug('MGMT_TRGTS > fetchManagementTargetData: e=', e)
      throw e
    }
  }

  /**
   * 稼働状況表示名を取得する
   *
   * @param {string} code 時間
   * @returns {string} 表示名
   */
  getDeviceStatusLabel(code: string): string {
    const format = {
      code: 'code',
      value: 'name'
    }

    return HelperService.getDefinitionName(this.deviceStatus, code, format)
  }

  /**
   * 節電モード表示名を取得する
   *
   * @param {string} code 時間
   * @returns {string} 表示名
   */
  getPowerSavingModeLabel(code: string): string {
    const format = {
      code: 'code',
      value: 'name'
    }

    return HelperService.getDefinitionName(this.managementPowerTypeList, code, format)
  }

  /**
   * 稼働状況背景色を取得する
   *
   * @param {string} code 時間
   * @returns {string} 表示名
   */
  getDeviceStatusBgColor(code: string): string {
    // 停止検知 | 停止中定期
    if (code === DeviceStatus.STOP_DETECTION || code === DeviceStatus.PERIODIC_OUTAGE) {
      return 'yellowBackgroundColor'
    }

    // 稼働開始 | 稼働中定期
    if (code === DeviceStatus.START_OPERATION || code === DeviceStatus.PERIODIC_DURING_OPERATION) {
      return 'blueBackgroundColor'
    }

    return ''
  }

  /**
   * 期間警告色を取得する(点検整備時期)
   *
   * @param {string[]} checkData 時間
   * @returns {string} 背景色
   */
  getBackGroundColorStyleByDateArrangement(checkData: string): string {
    const intervalTime = getIntervalTime(checkData)
    if (intervalTime === null) {
      return ''
    }

    if (intervalTime <= MaintenanceWarningDays.RED_WARNING_DAYS) {
      return 'redBackgroundColor'
    }

    if (
      intervalTime > MaintenanceWarningDays.RED_WARNING_DAYS &&
      intervalTime <= MaintenanceWarningDays.YELLOW_WARNING_DAYS
    ) {
      return 'yellowBackgroundColor'
    }

    return ''
  }

  /**
   * 期間警告色を取得する(車検時期)
   *
   * @param {string[]} checkData 時間
   * @returns {string} 背景色
   */
  getBackGroundColorStyleByDateCheck(checkData: string): string {
    const intervalTime = getIntervalTime(checkData)
    if (intervalTime === null) {
      return ''
    }

    if (intervalTime <= CheckCarWarningDays.RED_WARNING_DAYS) {
      return 'redBackgroundColor'
    }

    if (
      intervalTime > CheckCarWarningDays.RED_WARNING_DAYS &&
      intervalTime <= CheckCarWarningDays.YELLOW_WARNING_DAYS
    ) {
      return 'yellowBackgroundColor'
    }

    return ''
  }

  /**
   * 節電モード
   *
   * @param {string[]} powerSavingMode 時間
   * @returns {string} 背景色
   */
  getBackGroundColorStyle(powerSavingMode: string): string {
    if (!powerSavingMode) {
      return ''
    }

    if (powerSavingMode === PowerType.MODE1 || powerSavingMode === PowerType.MODE2) {
      return 'redBackgroundColor'
    }

    if (powerSavingMode === PowerType.EXCHANGE) {
      return 'yellowBackgroundColor'
    }

    return ''
  }

  /**
   * テーブル背景色を取得する
   *
   * @param {cellCallbackParams} item テーブルデータ
   * @returns {string} 背景色Class
   */
  getCellClassName(item: cellCallbackParams): string {
    const { property } = item.column as { property: string }
    const value = (item.row as Record<string, string>)[property]
    // 稼働状況
    if (property === 'deviceStatus') {
      return this.getDeviceStatusBgColor(value)
    }

    // 節電モード
    if (property === 'powerSavingMode') {
      return this.getBackGroundColorStyle(value)
    }

    // 点検整備時期
    if (property === 'arrangementDate') {
      return this.getBackGroundColorStyleByDateArrangement(value)
    }

    // 車検時期
    if (property === 'checkDate') {
      return this.getBackGroundColorStyleByDateCheck(value)
    }
    return ''
  }

  /**
   * 戻るボタンをクリックした時の処理
   *
   * @returns {void}
   */
  onBackButtonClick(): void {
    this.$log.debug('MGMT_TRGTS > onBackButtonClick')
    window.history.back()
  }

  /**
   * チェックボックスで選択したデータ
   */
  multipleSelection: Array<ManagementTarget> = []

  reallyOutputParams = {
    allSelect: false,
    businessOfficeIdList: [] as string[],
    managementTargetCodeList: [] as string[],
    startDate: '',
    endDate: ''
  } as ReallyOutputPostParam

  /**
   * テーブルのチェックしたデータを変更する
   *
   * @param {Array<ManagementTarget>} val チェックされたデータリスト
   * @returns {void}
   */
  handleSelectionChange(val: Array<ManagementTarget>): void {
    this.multipleSelection = val
  }

  /**
   * 実績出力ボタンをクリックした時の処理
   *
   * @returns {void}
   */
  async onReallyOutputButtonClick(): Promise<void> {
    this.$log.debug('MGMT_TRGTS > onReallyOutputButtonClick')
    if (!this.tableData || this.tableData.length === 0) {
      msgBoxService.info(i18n.t('messages.masterCommon.noDataToExport').toString())
      return
    }

    if (!this.multipleSelection || this.multipleSelection.length === 0) {
      msgBoxService.info(i18n.t('managementTarget.noSelected', [i18n.t('managementTarget.object')]).toString())
      return
    }

    this.reallyOutputParams = {
      allSelect: false,
      businessOfficeIdList: [] as string[],
      managementTargetCodeList: [] as string[],
      startDate: '',
      endDate: ''
    } as ReallyOutputPostParam

    if (this.tableData.length === this.multipleSelection.length) {
      this.reallyOutputParams.allSelect = true

      const businessOfficeIdList = new Set<string>()
      this.multipleSelection.forEach(item => {
        businessOfficeIdList.add(item.businessOfficeId)
      })
      this.reallyOutputParams.businessOfficeIdList = Array.from(businessOfficeIdList)
    } else {
      this.reallyOutputParams.allSelect = false
      this.multipleSelection.forEach(item => {
        if (!this.reallyOutputParams || !this.reallyOutputParams.managementTargetCodeList) {
          return
        }

        this.reallyOutputParams.managementTargetCodeList.push(item.manageTargetCode)
      })
    }

    this.showDialog = true
  }

  /**
   * 稼働実績
   */
  @Ref() readonly reallyOutputDialogFormRef!: Form

  /**
   * 稼働実績画面表示
   */
  showDialog = false

  /**
   * 表示状態変更
   *
   * @returns {void}
   */
  onHandelDisplay(): void {
    this.showDialog = !this.showDialog
  }

  reallyOutputDialogRules = {
    // 稼働開始日
    startDate: [
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('managementTarget.startDate')]).toString(),
        trigger: 'blur'
      }
    ],
    // 稼働終了日
    endDate: [
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('managementTarget.endDate')]).toString(),
        trigger: 'blur'
      }
    ]
  }

  /**
   * 出力前に、削除チェックを行う。
   *
   * @returns {boolean} チェック結果
   */
  // eslint-disable-next-line max-lines-per-function
  async dialogFormCheck(): Promise<boolean> {
    let valid = await this.reallyOutputDialogFormRef.validate()
    if (!valid) {
      return valid
    }

    // 稼働開始日は稼働終了日を超える場合
    if (isDateAfter(this.reallyOutputParams.startDate, this.reallyOutputParams.endDate)) {
      msgBoxService.info(
        i18n
          .t('vaildate.common.dateRange', [i18n.t('managementTarget.startDate'), i18n.t('managementTarget.endDate')])
          .toString()
      )
      return false
    }

    // 稼働開始日から稼働終了日までの選択された期間は1年を超える場合
    const diff = differenceInYears(
      parseDate(this.reallyOutputParams.endDate),
      parseDate(this.reallyOutputParams.startDate)
    )

    if (diff > 0) {
      msgBoxService.info(i18n.t('vaildate.common.differenceInYears', [1]).toString())
      return false
    }

    return true
  }

  /**
   * 出力ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  async onDialogOutputButtonClick(): Promise<void> {
    this.$log.debug(`MGMT_TRGTS > onDialogOutputButtonClick`)
    if (!(await this.dialogFormCheck())) {
      return
    }

    let res
    res = await ManagementTargetHistoryDownload(this.reallyOutputParams)
    HelperService.download(res.fileName, res.data)
  }

  /**
   * 出力ボタンをクリックした時の処理
   *
   * @returns {void}
   */
  async onOutputButtonClick(): Promise<void> {
    this.$log.debug('MGMT_TRGTS > onOutputButtonClick')
    if (!this.tableData || this.tableData.length === 0) {
      msgBoxService.info(i18n.t('messages.masterCommon.noDataToExport').toString())
      return
    }

    const params = {
      businessOfficeIdList: common.favOfficeList
    } as MasterGetParam

    const res = await managementTargetDataDownload(params)
    HelperService.download(res.fileName, res.data)
  }

  /**
   * 画面ツールチップを取得する
   *
   * @param {string} key ツールチップキー
   * @returns {any} ツールチップ
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getTooltip(key: string): any {
    return {
      content: i18n.t(`master.tooltipCommon.${key}`),
      placement: 'bottom-start',
      delay: { show: 1000, hide: 0 }
    }
  }

  // eslint-disable-next-line max-lines-per-function
  initSearchTargets(): void {
    this.searchTargets = [
      {
        type: FormItemType.Input,
        field: 'businessOfficeName'
      },
      {
        type: FormItemType.Input,
        field: 'manageTargetCode'
      },
      {
        type: FormItemType.Input,
        field: 'manageTargetName'
      },
      {
        type: FormItemType.Input,
        field: 'deviceStatus'
      },
      {
        type: FormItemType.Input,
        field: 'location'
      },
      {
        type: FormItemType.Input,
        field: 'managementPlaceDetail'
      },
      {
        type: FormItemType.Input,
        field: 'totalTime'
      },
      {
        type: FormItemType.Input,
        field: 'arrangementDate'
      },
      {
        type: FormItemType.Input,
        field: 'checkDate'
      },
      {
        type: FormItemType.Select,
        field: 'powerSavingMode',
        list: () => {
          return this.managementPowerTypeList
        }
      },
      {
        type: FormItemType.Input,
        field: 'owner'
      },
      {
        type: FormItemType.Input,
        field: 'lendBusinessOfficeCode'
      },
      {
        type: FormItemType.Input,
        field: 'lendBusinessOfficeName'
      },
      {
        type: FormItemType.Input,
        field: 'lendDate'
      },
      {
        type: FormItemType.Input,
        field: 'lastUpdateDate'
      }
    ]
  }

  /**
   * 当前ページを変更するときの処理
   *
   * @param {number} page 当前ページ
   * @returns {void}
   */
  onCurrentPageChange(page: number): void {
    this.setVisableTableData(page)
  }

  /**
   * 検索BOXをクリックしたときの処理
   *
   * @returns {void}
   */
  onSearchBoxClick(): void {
    this.searchText = this.searchText.replace(/[,]/g, '')

    if (!this.searchText) {
      this.initSearchBox()
      return
    }

    if (this.oldSearchText === this.searchText) {
      this.doSearchBoxHighlight()
      return
    }

    // 命中したデータリストを作成する
    this.searchResultIndex = -1
    this.searchResultPoint = -1
    this.searchResultIndexList = []
    this.oldSearchText = ''
    this.searchTargetIndexByTextBox(this.tableData)
    if (this.searchResultIndexList.length === 0) {
      msgBoxService.info(i18n.t('messages.masterCommon.noSearchBoxResult').toString())
      return
    }

    this.oldSearchText = this.searchText
    this.doSearchBoxHighlight()
  }

  /**
   * 検索結果のHighlight
   *
   * @returns {void}
   */
  doSearchBoxHighlight(): void {
    this.toNextSearchResultPoint()
    this.handleSearchResultHighlight()
    this.scrollToHighlightRow()
  }

  /**
   * 検索BOXをクリックしたときの処理
   *
   * @param {Array<any>} targetTableData 対象データ
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchTargetIndexByTextBox(targetTableData: Array<any>): void {
    targetTableData.forEach((item, index) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const target: any[] = []
      this.searchTargets.forEach(searchTargets => {
        const { type, field, list } = searchTargets
        if (type === FormItemType.Input) {
          target.push(item[field])
        } else if (list) {
          const generalList = list()
          const format = {
            code: 'code',
            value: 'name'
          }
          target.push(HelperService.getDefinitionName(generalList, item[field], format))
        }
      })

      const itemContext = target.toString()

      if (itemContext.includes(this.searchText)) {
        this.searchResultIndexList.push(index)
      }
    })
  }

  /**
   * 次の検索結果へ
   *
   * @returns {void}
   */
  toNextSearchResultPoint(): void {
    const targetsLength = this.searchResultIndexList.length
    if (this.searchResultPoint === targetsLength - 1) {
      this.searchResultPoint = 0
    } else {
      this.searchResultPoint++
    }
  }

  /**
   * 検索結果のページと位置を確定
   *
   * @returns {void}
   */
  handleSearchResultHighlight(): void {
    if (this.searchResultIndexList.length === 0) {
      return
    }

    const targetindex = this.searchResultIndexList[this.searchResultPoint]
    // 目標のページを算出する
    this.searchResultIndex = targetindex % this.visablePageSize
    const targetPage = Math.ceil((targetindex + 1) / this.visablePageSize) || 1
    if (this.currentPage !== targetPage) {
      this.currentPage = targetPage
      this.onCurrentPageChange(this.currentPage)
    }
    this.$log.debug(this.searchResultIndex)
  }

  scrollToHighlightRow(): void {
    this.$nextTick(() => {
      const targetId = `tableData[${this.searchResultIndex}].${this.firstField}`
      const target = document.getElementById(targetId)
      target?.scrollIntoView()
    })
  }

  /**
   * テーブル行CLASS（検索用、rewriteの場合、必ずこの機能を流用してください。）
   *
   * @param {any} rowData 対象データ
   * @returns {string} 目標CLASS
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  tableRowClassName(rowData: any): string {
    const { rowIndex } = rowData
    if (this.searchResultIndex === -1) {
      return ''
    }

    if (this.searchResultIndex === rowIndex) {
      return 'search-result-row'
    }

    return ''
  }
}
</script>

<style lang="scss" scoped>
::v-deep .cell {
  text-overflow: unset;
}

::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

::v-deep .redBackgroundColor {
  background-color: $C23 !important;
}

::v-deep .yellowBackgroundColor {
  background-color: $C24 !important;
}

::v-deep .blueBackgroundColor {
  background-color: $C32 !important;
}

.tti-main-header-shadow {
  display: flex;
  justify-content: space-between;
}

.pull-left {
  width: 100%;
}

.pos-rel {
  position: relative;

  .notes-title {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
  }
}

.tti-btn-jump {
  background-color: $C43;
  height: 100%;
  float: right;
  padding: 0 6px;

  &:active {
    @include bt_001_p;
  }
}
</style>
